import { type Brand, type BrandResponse, useBrands } from "@/modules/brand";
import * as Dialog from "@radix-ui/react-dialog";
import { useState, useEffect } from "react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import Button from "@/components/ui/button";
import Input from "@/components/ui/input";
import { useAuth } from "@/modules/auth";

const toKebabCase = (str: string): string => {
	return str
		.toLowerCase()
		.trim()
		.replace(/[^\w\s-]/g, "") // Remove special characters
		.replace(/\s+/g, "-") // Replace spaces with hyphens
		.replace(/-+/g, "-") // Replace multiple hyphens with single hyphen
		.replace(/^-|-$/g, ""); // Remove leading/trailing hyphens
};

const createBrandSchema = z.object({
	name: z.string().min(1, "Brand name is required"),
	slug: z
		.string()
		.min(3, "Slug must be at least 3 characters")
		.regex(/^[a-z0-9-]+$/, "Slug can only contain lowercase letters, numbers, and hyphens")
		.regex(/^[^-]/, "Slug cannot start with a hyphen")
		.regex(/[^-]$/, "Slug cannot end with a hyphen")
		.regex(/^(?!.*-{2})/, "Slug cannot contain two or more consecutive hyphens"),
	website_url: z.string().min(1, "Website URL/Social Media URL is required").url("Please enter a valid URL"),
});

const editBrandSchema = z.object({
	name: z.string().min(1, "Brand name is required"),
	slug: z
		.string()
		.min(3, "Slug must be at least 3 characters")
		.regex(/^[a-z0-9-]+$/, "Slug can only contain lowercase letters, numbers, and hyphens")
		.regex(/^[^-]/, "Slug cannot start with a hyphen")
		.regex(/[^-]$/, "Slug cannot end with a hyphen")
		.regex(/^(?!.*-{2})/, "Slug cannot contain two or more consecutive hyphens"),
	website_url: z.string().min(1, "Website URL/Social Media URL is required").url("Please enter a valid URL"),
});

type CreateBrandFormData = z.infer<typeof createBrandSchema>;
type EditBrandFormData = z.infer<typeof editBrandSchema>;

interface BrandModalProps {
	open: boolean;
	mode: "create" | "edit";
	brand?: Brand | null;
	onOpenChange: (open: boolean) => void;
	onSuccess: (brand: BrandResponse | Brand) => void;
}

export default function BrandModal({ open, mode, brand, onOpenChange, onSuccess }: BrandModalProps) {
	const { createBrand, updateBrand, checkBrandSlugAvailability } = useBrands();
	const { user } = useAuth();
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [isSlugManuallyEdited, setIsSlugManuallyEdited] = useState(false);
	const [isCheckingSlug, setIsCheckingSlug] = useState(false);
	const [slugError, setSlugError] = useState<string | null>(null);
	const [isSlugAvailable, setIsSlugAvailable] = useState(false);
	const [originalSlug, setOriginalSlug] = useState<string | null>(null);

	const isCreateMode = mode === "create";

	const createForm = useForm<CreateBrandFormData>({
		resolver: zodResolver(createBrandSchema),
		mode: "onChange",
		defaultValues: {
			name: "",
			slug: "",
			website_url: "",
		},
	});

	const editForm = useForm<EditBrandFormData>({
		resolver: zodResolver(editBrandSchema),
		mode: "onChange",
		defaultValues: {
			name: "",
			slug: "",
			website_url: "",
		},
	});

	const name = isCreateMode ? createForm.watch("name") : editForm.watch("name");
	const slug = isCreateMode ? createForm.watch("slug") : editForm.watch("slug") || "";
	const errors = isCreateMode ? createForm.formState.errors : editForm.formState.errors;

	// Initialize form with brand data when editing
	useEffect(() => {
		if (mode === "edit" && brand && open) {
			const brandSlug = toKebabCase(brand.name || "");
			editForm.reset({
				name: brand.name || "",
				slug: brandSlug,
				website_url: brand.website_url || "",
			});
			setOriginalSlug(brandSlug);
			setIsSlugManuallyEdited(false);
			setSlugError(null);
			setIsSlugAvailable(false);
		} else if (mode === "create" && open) {
			createForm.reset({
				name: "",
				slug: "",
				website_url: "",
			});
			setOriginalSlug(null);
			setIsSlugManuallyEdited(false);
			setSlugError(null);
			setIsSlugAvailable(false);
		}
	}, [brand, mode, open, editForm, createForm]);

	// Auto-generate slug from name
	useEffect(() => {
		if (!isSlugManuallyEdited) {
			const newSlug = toKebabCase(name);
			setSlugError(null);
			if (isCreateMode) {
				createForm.clearErrors("slug");
				createForm.setValue("slug", newSlug);
			} else {
				editForm.clearErrors("slug");
				editForm.setValue("slug", newSlug);
			}
		}
	}, [name, isSlugManuallyEdited, isCreateMode, createForm, editForm]);

	// Check slug availability
	useEffect(() => {
		const isValidSlug = slug && slug.length >= 3 && /^[a-z0-9-]+$/.test(slug) && /^[^-]/.test(slug) && /[^-]$/.test(slug) && !/.*-{2}/.test(slug);

		if (!slug || !isValidSlug) {
			setSlugError(null);
			setIsSlugAvailable(false);
			return;
		}

		// Skip availability check in edit mode only if slug matches the original
		if (!isCreateMode) {
			if (originalSlug && slug === originalSlug) {
				setSlugError(null);
				setIsSlugAvailable(false); // Don't show "available" message for original slug
				return;
			}
		}

		setIsSlugAvailable(false);
		setSlugError(null);
		const timeoutId = setTimeout(async () => {
			setIsCheckingSlug(true);
			try {
				const isAvailable = await checkBrandSlugAvailability(slug);
				if (isAvailable) {
					setIsSlugAvailable(true);
				} else {
					setSlugError("This slug is already taken");
				}
			} catch {
				setSlugError("Failed to check slug availability");
			} finally {
				setIsCheckingSlug(false);
			}
		}, 500);

		return () => clearTimeout(timeoutId);
	}, [slug, isCreateMode, isSlugManuallyEdited, originalSlug, checkBrandSlugAvailability]);

	const onSubmit = async (data: CreateBrandFormData | EditBrandFormData) => {
		setIsLoading(true);
		setError(null);

		try {
			if (isCreateMode) {
				const createData = data as CreateBrandFormData;
				const result = await createBrand(createData);

				if (result) {
					onSuccess(result);
					resetForm();
					onOpenChange(false);
				} else {
					setError("Failed to create brand. Please try again.");
				}
			} else {
				if (!brand) return;

				const editData = data as EditBrandFormData;
				const updateData: { name: string; slug: string; website_url: string } = {
					name: editData.name.trim(),
					slug: editData.slug.trim(),
					website_url: editData.website_url.trim(),
				};
				const result = await updateBrand(brand.id, updateData);

				if (result) {
					onSuccess(result.data!);
					onOpenChange(false);
				} else {
					setError("Failed to update brand. Please try again.");
				}
			}
		} catch {
			setError(isCreateMode ? "An error occurred while creating the brand." : "An error occurred while updating the brand.");
		} finally {
			setIsLoading(false);
		}
	};

	const resetForm = () => {
		if (isCreateMode) {
			createForm.reset();
		} else {
			editForm.reset();
		}
		setIsSlugManuallyEdited(false);
		setSlugError(null);
		setIsSlugAvailable(false);
		setError(null);
	};

	const handleOpenChange = (newOpen: boolean) => {
		if (!newOpen) {
			resetForm();
		}
		onOpenChange(newOpen);
	};

	const title = isCreateMode ? "Create Brand" : "Edit Brand";
	const description = isCreateMode ? "Add a new brand to manage your brand." : "Update your brand details.";
	const submitButtonText = isLoading ? (isCreateMode ? "Creating..." : "Saving...") : isCreateMode ? "Create Brand" : "Save Changes";

	return (
		<Dialog.Root open={open} onOpenChange={handleOpenChange}>
			<Dialog.Portal>
				<Dialog.Overlay className="fixed inset-0 bg-black/50 z-50 modal-overlay" />
				<Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-xl z-50 w-full max-w-lg modal-content overflow-hidden">
					<form onSubmit={isCreateMode ? createForm.handleSubmit(onSubmit) : editForm.handleSubmit(onSubmit)} className="flex flex-col max-h-[80vh]">
						{/* Header */}
						<div className="px-6 pt-6 pb-4 border-b border-gray-200">
							<Dialog.Title className="text-base font-semibold text-gray-900 leading-base">{title}</Dialog.Title>
							<Dialog.Description className="text-sm text-gray-500 leading-sm mt-1">{description}</Dialog.Description>
						</div>

						{/* Content */}
						<div className="overflow-y-auto px-6 py-4">
							{isCreateMode && (() => {
								const partner = user?.partner;
								const trialDays = partner?.trial_duration_days;
								const hasPartnerTrial = !!trialDays;

								return hasPartnerTrial ? (
									<div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
										<div className="flex items-start gap-3">
											<div className="flex-shrink-0 rounded-full bg-emerald-100 p-2">
												<svg className="h-5 w-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
													<path strokeLinecap="round" strokeLinejoin="round" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
												</svg>
											</div>
											<div>
												<h3 className="text-sm font-semibold text-emerald-800">You've unlocked a {trialDays}-day free trial through {partner.name}!</h3>
												<p className="mt-1 text-sm text-emerald-700">
													Enjoy full access to our best plan with unlimited text and video testimonials. No credit card required.
												</p>
											</div>
										</div>
									</div>
								) : null;
							})()}
							<div className="space-y-4">
								<Input
									id={`${mode}-brand-name`}
									label="Brand Name"
									type="text"
									placeholder="Enter brand name"
									required
									disabled={isLoading}
									error={errors.name?.message}
									{...(isCreateMode ? createForm.register("name") : editForm.register("name"))}
								/>

								<Input
									id="brand-slug"
									label="Your Public URL"
									type="text"
									placeholder="e.g., my-company"
									disabled={isLoading}
									error={isCreateMode ? (errors as typeof createForm.formState.errors).slug?.message : errors.slug?.message}
									inputPrefix={<span className="text-tertiary text-sm">whatpeople.sayabout.us/</span>}
									message={
										slug && !(isCreateMode ? (errors as typeof createForm.formState.errors).slug?.message : errors.slug?.message)
											? isCheckingSlug
												? { text: "Checking availability...", status: "loading" }
												: slugError
												? { text: slugError, status: "error" }
												: isSlugAvailable
												? { text: "This URL is available!", status: "success" }
												: { text: "Enter a unique identifier for your public page", status: "message" }
											: { text: "Enter a unique identifier for your public page", status: "message" }
									}
									{...(isCreateMode
										? createForm.register("slug", {
												onChange: () => setIsSlugManuallyEdited(true),
										  })
										: editForm.register("slug", {
												onChange: () => setIsSlugManuallyEdited(true),
										  }))}
									required
								/>

								<Input
									id={`${mode}-brand-url`}
									label="Website URL/Social Media URL"
									type="url"
									placeholder="https://example.com or https://x.com/example"
									disabled={isLoading}
									error={errors.website_url?.message}
									{...(isCreateMode ? createForm.register("website_url") : editForm.register("website_url"))}
									required
								/>
							</div>
						</div>

						{/* Footer */}
						<div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
							<Dialog.Close asChild>
								<Button type="button" variant="soft" disabled={isLoading}>
									Cancel
								</Button>
							</Dialog.Close>
							<div className="flex items-center gap-3">
								{error && <p className="text-sm text-red-600 leading-sm">{error}</p>}
								<Button type="submit" variant="brand" disabled={isLoading || isCheckingSlug || !!slugError}>
									{submitButtonText}
								</Button>
							</div>
						</div>
					</form>

					<Dialog.Close asChild>
						<button className="absolute top-7 right-6 text-gray-400 hover:text-gray-600 transition-colors" aria-label="Close">
							<svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
							</svg>
						</button>
					</Dialog.Close>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	);
}
